import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PertandinganPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedCompetitionName, setSelectedCompetitionName] = useState('');
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  
  // Match Detail Modal State
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchDetail, setMatchDetail] = useState(null);
  const [matchTimeline, setMatchTimeline] = useState([]);
  const [matchLineup, setMatchLineup] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  React.useEffect(() => {
    fetchCompetitions();
  }, []);

  React.useEffect(() => {
    if (selectedCompetition) {
      fetchMatches(selectedCompetition);
    }
  }, [selectedCompetition]);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/lapangbola/competitions`);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        setCompetitions(response.data.data);
        const firstComp = response.data.data[0];
        setSelectedCompetition(firstComp.id);
        setSelectedCompetitionName(firstComp.name);
      } else {
        setCompetitions([]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error('Gagal memuat data kompetisi');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async (competitionId) => {
    try {
      setLoadingMatches(true);
      const response = await axios.get(`${API}/api/lapangbola/matches`, {
        params: { competition_id: competitionId }
      });
      
      if (response.data && response.data.data) {
        // Sort: upcoming first (by date asc), then finished (by date desc)
        const allMatches = response.data.data;
        const upcoming = allMatches.filter(m => m.match_status !== 'Finished')
          .sort((a, b) => new Date(a.default_match_date || a.match_date) - new Date(b.default_match_date || b.match_date));
        const finished = allMatches.filter(m => m.match_status === 'Finished')
          .sort((a, b) => new Date(b.match_date || b.default_match_date) - new Date(a.match_date || a.default_match_date));
        
        setMatches([...upcoming, ...finished]);
      } else {
        setMatches([]);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Gagal memuat data pertandingan');
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const fetchMatchDetail = async (matchId) => {
    try {
      setLoadingDetail(true);
      
      const [detailRes, timelineRes, lineupRes] = await Promise.all([
        axios.get(`${API}/api/lapangbola/matches/${matchId}`),
        axios.get(`${API}/api/lapangbola/matches/${matchId}/timeline`),
        axios.get(`${API}/api/lapangbola/lineup`, { params: { match_id: matchId } })
      ]);
      
      if (detailRes.data) {
        setMatchDetail(detailRes.data);
      }
      
      if (timelineRes.data && timelineRes.data.data) {
        setMatchTimeline(timelineRes.data.data);
      }
      
      if (lineupRes.data && lineupRes.data.collection) {
        setMatchLineup(lineupRes.data.collection);
      }
    } catch (error) {
      console.error('Error fetching match detail:', error);
      toast.error('Gagal memuat detail pertandingan');
    } finally {
      setLoadingDetail(false);
    }
  };

  const openMatchDetail = (match) => {
    // Allow opening detail for both Finished and Ongoing matches
    if (match.match_status === 'Finished' || match.match_status === 'Ongoing' || match.match_status === 'Live') {
      setSelectedMatch(match);
      fetchMatchDetail(match.id);
    }
  };

  const refreshMatchDetail = () => {
    if (selectedMatch) {
      fetchMatchDetail(selectedMatch.id);
      toast.success('Data pertandingan diperbarui');
    }
  };

  const closeMatchDetail = () => {
    setSelectedMatch(null);
    setMatchDetail(null);
    setMatchTimeline([]);
    setMatchLineup([]);
  };

  const handleCompetitionChange = (competitionId) => {
    const comp = competitions.find(c => c.id === Number(competitionId));
    setSelectedCompetition(Number(competitionId));
    setSelectedCompetitionName(comp?.name || '');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  // Match Detail Modal Component
  const MatchDetailModal = () => {
    const [detailTab, setDetailTab] = React.useState('statistik');
    
    if (!selectedMatch) return null;

    const homeLineup = matchLineup.filter(p => p.club_id === selectedMatch.home_club_id);
    const awayLineup = matchLineup.filter(p => p.club_id === selectedMatch.away_club_id);

    const getEventIcon = (statName) => {
      const icons = {
        'goal': '⚽',
        'penalty_goal': '⚽🎯',
        'own_goal': '⚽🔴',
        'yellow': '🟨',
        'yellow_card': '🟨',
        'red': '🟥',
        'red_card': '🟥',
        'second_yellow': '🟨🟥',
        'substitution': '🔄',
        'sub_in': '➡️',
        'sub_out': '⬅️',
        'assist': '👟',
        'save': '🧤',
        'penalty_miss': '❌',
        'var': '📺',
      };
      return icons[statName?.toLowerCase()] || '📌';
    };

    const getStageLabel = (stage) => {
      const stages = {
        '1st': 'Babak 1',
        '2nd': 'Babak 2',
        'first_half': 'Babak 1',
        'second_half': 'Babak 2',
        'extra_time': 'Extra Time',
        'penalty': 'Adu Penalti',
      };
      return stages[stage?.toLowerCase()] || stage || '';
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 pb-20" onClick={closeMatchDetail}>
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-gradient-ltb-r text-white p-4 rounded-t-xl sticky top-0 z-10">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-bold">Detail Pertandingan</h2>
                <p className="text-white/80 text-sm">{selectedMatch.copa_round_name || selectedCompetitionName}</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Refresh button for ongoing matches */}
                {(selectedMatch.match_status === 'Ongoing' || selectedMatch.match_status === 'Live') && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); refreshMatchDetail(); }}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                    title="Refresh Data"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
                <button onClick={closeMatchDetail} className="text-white/80 hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {loadingDetail ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ltb-bright mx-auto"></div>
              <p className="mt-3 text-gray-600">Memuat detail...</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* 1. Score Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-center text-sm text-gray-500 mb-3">
                  {formatDate(selectedMatch.match_date || selectedMatch.default_match_date)}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex-1 text-center">
                    <img 
                      src={selectedMatch.home_picture_url} 
                      alt={selectedMatch.home_club_name}
                      className="w-16 h-16 mx-auto object-contain mb-2"
                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>'; }}
                    />
                    <p className="font-medium text-sm">{selectedMatch.home_club_name}</p>
                  </div>
                  <div className="px-4 text-center">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl font-bold text-gray-800">{selectedMatch.home_score}</span>
                      <span className="text-2xl text-gray-400">-</span>
                      <span className="text-3xl font-bold text-gray-800">{selectedMatch.away_score}</span>
                    </div>
                    <span className="text-xs text-green-600 font-medium">FT</span>
                  </div>
                  <div className="flex-1 text-center">
                    <img 
                      src={selectedMatch.away_picture_url} 
                      alt={selectedMatch.away_club_name}
                      className="w-16 h-16 mx-auto object-contain mb-2"
                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>'; }}
                    />
                    <p className="font-medium text-sm">{selectedMatch.away_club_name}</p>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex space-x-2 border-b border-gray-200">
                <button
                  onClick={() => setDetailTab('statistik')}
                  className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                    detailTab === 'statistik'
                      ? 'border-ltb-bright text-ltb-bright'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📊 Statistik & Lineup
                </button>
                <button
                  onClick={() => setDetailTab('timeline')}
                  className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
                    detailTab === 'timeline'
                      ? 'border-ltb-bright text-ltb-bright'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  ⏱️ Timeline
                </button>
              </div>

              {/* Tab Content: Statistik & Lineup */}
              {detailTab === 'statistik' && (
                <div className="space-y-4">
                  {/* Statistics Section - Updated list */}
                  {matchDetail && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">📊</span> Statistik Pertandingan
                      </h3>
                      <div className="space-y-3 bg-gray-50 rounded-lg p-3">
                        {[
                          { label: 'Ball Possession', home: matchDetail.poss_home || 0, away: matchDetail.poss_away || 0, suffix: '%' },
                          { label: 'Total Shots', home: (matchDetail.shooton_home || 0) + (matchDetail.shootoff_home || 0), away: (matchDetail.shooton_away || 0) + (matchDetail.shootoff_away || 0) },
                          { label: 'Shots on Target', home: matchDetail.shooton_home || 0, away: matchDetail.shooton_away || 0 },
                          { label: 'Passes', home: matchDetail.pass_home || 0, away: matchDetail.pass_away || 0 },
                          { label: 'Pass Accuracy', home: matchDetail.pass_home > 0 ? Math.round((matchDetail.pass_home / (matchDetail.pass_home + (matchDetail.passfail_home || 0))) * 100) : 0, away: matchDetail.pass_away > 0 ? Math.round((matchDetail.pass_away / (matchDetail.pass_away + (matchDetail.passfail_away || 0))) * 100) : 0, suffix: '%' },
                          { label: 'Fouls', home: matchDetail.foul_home || 0, away: matchDetail.foul_away || 0 },
                          { label: 'Tackles', home: matchDetail.tackle_home || 0, away: matchDetail.tackle_away || 0 },
                          { label: 'Intercepts', home: matchDetail.intercept_home || 0, away: matchDetail.intercept_away || 0 },
                          { label: 'Blocks', home: matchDetail.shootblock_home || 0, away: matchDetail.shootblock_away || 0 },
                          { label: 'Saves', home: matchDetail.matchsave_home || 0, away: matchDetail.matchsave_away || 0 },
                        ].map((stat, idx) => (
                          <div key={idx} className="flex items-center">
                            <span className="w-8 text-right text-sm font-bold text-blue-600">{stat.home}{stat.suffix || ''}</span>
                            <div className="flex-1 mx-3">
                              <div className="flex h-2 rounded-full overflow-hidden bg-gray-300">
                                <div 
                                  className="bg-blue-500" 
                                  style={{ width: `${stat.home + stat.away > 0 ? (stat.home / (stat.home + stat.away)) * 100 : 50}%` }}
                                />
                                <div 
                                  className="bg-red-500" 
                                  style={{ width: `${stat.home + stat.away > 0 ? (stat.away / (stat.home + stat.away)) * 100 : 50}%` }}
                                />
                              </div>
                              <p className="text-xs text-center text-gray-600 mt-1 font-medium">{stat.label}</p>
                            </div>
                            <span className="w-8 text-left text-sm font-bold text-red-600">{stat.away}{stat.suffix || ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lineup Section */}
                  {matchLineup.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">👥</span> Susunan Pemain
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {/* Home Team */}
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-sm font-semibold text-blue-700 mb-2 text-center">{selectedMatch.home_club_name}</p>
                          <div className="space-y-1">
                            {homeLineup.filter(p => p.starting === 1).slice(0, 11).map((player, idx) => (
                              <div key={idx} className="flex items-center text-xs bg-white rounded px-2 py-1.5 shadow-sm">
                                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-2">
                                  {player.number || '-'}
                                </span>
                                <span className="flex-1 truncate font-medium">{player.name}</span>
                                {player.goal > 0 && <span className="ml-1">⚽</span>}
                              </div>
                            ))}
                          </div>
                          {homeLineup.filter(p => p.starting !== 1).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-blue-200">
                              <p className="text-xs text-blue-600 mb-1 font-medium">Cadangan:</p>
                              <div className="space-y-1">
                                {homeLineup.filter(p => p.starting !== 1).slice(0, 7).map((player, idx) => (
                                  <div key={idx} className="flex items-center text-xs text-gray-600 px-2 py-1">
                                    <span className="w-5 text-gray-400 mr-1">{player.number || '-'}</span>
                                    <span className="truncate">{player.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Away Team */}
                        <div className="bg-red-50 rounded-lg p-3">
                          <p className="text-sm font-semibold text-red-700 mb-2 text-center">{selectedMatch.away_club_name}</p>
                          <div className="space-y-1">
                            {awayLineup.filter(p => p.starting === 1).slice(0, 11).map((player, idx) => (
                              <div key={idx} className="flex items-center text-xs bg-white rounded px-2 py-1.5 shadow-sm">
                                <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center font-bold mr-2">
                                  {player.number || '-'}
                                </span>
                                <span className="flex-1 truncate font-medium">{player.name}</span>
                                {player.goal > 0 && <span className="ml-1">⚽</span>}
                              </div>
                            ))}
                          </div>
                          {awayLineup.filter(p => p.starting !== 1).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-red-200">
                              <p className="text-xs text-red-600 mb-1 font-medium">Cadangan:</p>
                              <div className="space-y-1">
                                {awayLineup.filter(p => p.starting !== 1).slice(0, 7).map((player, idx) => (
                                  <div key={idx} className="flex items-center text-xs text-gray-600 px-2 py-1">
                                    <span className="w-5 text-gray-400 mr-1">{player.number || '-'}</span>
                                    <span className="truncate">{player.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab Content: Timeline */}
              {detailTab === 'timeline' && (
                <div>
                  {matchTimeline.length > 0 ? (
                    <div className="space-y-2">
                      {matchTimeline.map((event, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-start p-3 rounded-lg border-l-4 ${
                            event.home_away === 'home' 
                              ? 'bg-blue-50 border-blue-500' 
                              : 'bg-red-50 border-red-500'
                          }`}
                        >
                          <div className="w-12 flex-shrink-0 text-center">
                            <span className="inline-flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-sm">
                              <span className="text-sm font-bold text-gray-700">{event.time_minute}'</span>
                            </span>
                          </div>
                          
                          <div className="flex-1 ml-3">
                            <div className="flex items-center space-x-2">
                              <span className="text-xl">{getEventIcon(event.statistic_name)}</span>
                              <div>
                                <p className="font-semibold text-gray-800">{event.player_name}</p>
                                <p className="text-xs text-gray-500">{event.club_name}</p>
                              </div>
                            </div>
                            
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                                event.statistic_name === 'goal' ? 'bg-green-100 text-green-700' :
                                event.statistic_name === 'yellow' || event.statistic_name === 'yellow_card' ? 'bg-yellow-100 text-yellow-700' :
                                event.statistic_name === 'red' || event.statistic_name === 'red_card' ? 'bg-red-100 text-red-700' :
                                event.statistic_name === 'substitution' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {event.statistic_name?.replace(/_/g, ' ').toUpperCase() || 'EVENT'}
                              </span>
                              
                              {event.stage && (
                                <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
                                  {getStageLabel(event.stage)}
                                </span>
                              )}
                            </div>
                            
                            {event.desc && (
                              <p className="mt-2 text-sm text-gray-600 italic">
                                "{event.desc}"
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-gray-500">Timeline tidak tersedia</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Separate matches into upcoming and finished
  const upcomingMatches = matches.filter(m => m.match_status !== 'Finished');
  const finishedMatches = matches.filter(m => m.match_status === 'Finished');

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-ltb-r text-white p-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="mb-2 flex items-center text-white/80 hover:text-white"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>
          <h1 className="text-2xl font-bold">Pertandingan</h1>
          <p className="text-white/80 text-sm mt-1">Jadwal dan hasil pertandingan</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Competition Selector */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Kompetisi</label>
          <select
            value={selectedCompetition || ''}
            onChange={(e) => handleCompetitionChange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
          >
            {competitions.map(comp => (
              <option key={comp.id} value={comp.id}>{comp.name}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        {loadingMatches ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto mb-4"></div>
            <p className="text-gray-600">Memuat data pertandingan...</p>
          </div>
        ) : matches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600 text-lg">Belum ada data pertandingan</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Competition Title */}
            {selectedCompetitionName && (
              <div className="bg-gradient-ltb-r text-white px-4 py-3 rounded-lg">
                <h2 className="text-lg font-semibold">{selectedCompetitionName}</h2>
              </div>
            )}

            {/* Upcoming Matches Section */}
            {upcomingMatches.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                  Akan Datang / Sedang Berlangsung
                </h3>
                <div className="space-y-3">
                  {upcomingMatches.map((match) => {
                    const isOngoing = match.match_status === 'Ongoing' || match.match_status === 'Live';
                    return (
                    <div 
                      key={match.id} 
                      className={`bg-white rounded-lg shadow-md overflow-hidden border-l-4 ${
                        isOngoing ? 'border-orange-500 cursor-pointer hover:shadow-lg' : 'border-blue-500'
                      } transition-shadow`}
                      onClick={isOngoing ? () => openMatchDetail(match) : undefined}
                    >
                      {/* Match Date Header */}
                      <div className={`${isOngoing ? 'bg-orange-50' : 'bg-blue-50'} px-4 py-2 border-b`}>
                        <div className="flex justify-between items-center">
                          <span className={`text-sm font-medium ${isOngoing ? 'text-orange-700' : 'text-blue-700'}`}>
                            {formatDate(match.default_match_date || match.match_date)}
                          </span>
                          <span className={`text-sm font-bold ${isOngoing ? 'text-orange-600' : 'text-blue-600'}`}>
                            {formatTime(match.default_match_date || match.match_date)} WIB
                          </span>
                        </div>
                        {match.copa_round_name && (
                          <span className={`text-xs ${isOngoing ? 'text-orange-500' : 'text-blue-500'}`}>{match.copa_round_name}</span>
                        )}
                      </div>
                      
                      {/* Match Content */}
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 text-center">
                            <img 
                              src={match.home_picture_url} 
                              alt={match.home_club_name}
                              className="w-12 h-12 mx-auto object-contain mb-2"
                              onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>'; }}
                            />
                            <p className="text-sm font-medium text-gray-800">{match.home_club_name}</p>
                          </div>
                          
                          <div className="px-4 text-center">
                            {isOngoing ? (
                              <>
                                <div className="flex items-center justify-center space-x-2">
                                  <span className="text-2xl font-bold text-gray-800">{match.home_score ?? 0}</span>
                                  <span className="text-lg text-gray-400">-</span>
                                  <span className="text-2xl font-bold text-gray-800">{match.away_score ?? 0}</span>
                                </div>
                                <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full animate-pulse">
                                  🔴 LIVE
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="text-xl font-bold text-gray-400">VS</span>
                                <p className="text-xs text-blue-500 mt-1">{match.match_status || 'Scheduled'}</p>
                              </>
                            )}
                          </div>
                          
                          <div className="flex-1 text-center">
                            <img 
                              src={match.away_picture_url} 
                              alt={match.away_club_name}
                              className="w-12 h-12 mx-auto object-contain mb-2"
                              onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>'; }}
                            />
                            <p className="text-sm font-medium text-gray-800">{match.away_club_name}</p>
                          </div>
                        </div>
                        {isOngoing && (
                          <p className="text-center text-xs text-orange-600 mt-2 font-medium">
                            👆 Klik untuk lihat detail
                          </p>
                        )}
                      </div>
                    </div>
                  )})}
                </div>
              </div>
            )}

            {/* Finished Matches Section */}
            {finishedMatches.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  Selesai
                </h3>
                <div className="space-y-3">
                  {finishedMatches.map((match) => (
                    <div 
                      key={match.id} 
                      className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-green-500 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => openMatchDetail(match)}
                    >
                      {/* Match Date Header */}
                      <div className="bg-green-50 px-4 py-2 border-b">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-green-700">
                            {formatDate(match.match_date || match.default_match_date)}
                          </span>
                          {match.copa_round_name && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                              {match.copa_round_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Match Content */}
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 text-center">
                            <img 
                              src={match.home_picture_url} 
                              alt={match.home_club_name}
                              className="w-12 h-12 mx-auto object-contain mb-2"
                              onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>'; }}
                            />
                            <p className="text-sm font-medium text-gray-800">{match.home_club_name}</p>
                          </div>
                          
                          <div className="px-4 text-center">
                            <div className="flex items-center space-x-2">
                              <span className={`text-2xl font-bold ${match.home_score > match.away_score ? 'text-green-600' : match.home_score < match.away_score ? 'text-gray-400' : 'text-gray-700'}`}>
                                {match.home_score}
                              </span>
                              <span className="text-gray-400 text-xl">-</span>
                              <span className={`text-2xl font-bold ${match.away_score > match.home_score ? 'text-green-600' : match.away_score < match.home_score ? 'text-gray-400' : 'text-gray-700'}`}>
                                {match.away_score}
                              </span>
                            </div>
                            <span className="text-xs text-green-600 font-medium">FT</span>
                          </div>
                          
                          <div className="flex-1 text-center">
                            <img 
                              src={match.away_picture_url} 
                              alt={match.away_club_name}
                              className="w-12 h-12 mx-auto object-contain mb-2"
                              onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="12" r="10"/></svg>'; }}
                            />
                            <p className="text-sm font-medium text-gray-800">{match.away_club_name}</p>
                          </div>
                        </div>
                        
                        <p className="text-center text-xs text-ltb-bright mt-3">Tap untuk lihat detail →</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Match Detail Modal */}
      <MatchDetailModal />
    </div>
  );
};

export default PertandinganPage;
